import { fromJS } from 'immutable';

import AssetManager from 'AssetManager';
import ScopeBindingManager from 'ScopeBindingManager';
import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import { BOUND_REFERENCES, RESOLVED } from 'ScopeBindingManager/constants';
import ConfigResolver from '../../resolvers/RTMResolvers/ConfigResolver';
import { getSchemaVersion } from '../../utils/Schema';
import { isEqual } from 'lodash';

const URN_EXTERNAL = 'urn:external';
const ASSET = 'asset';

const enhanceWithConfigResolver = (service, configData) => {
  const configResolver = new ConfigResolver(service, configData);
  const isBindingResolveToRealValueEnabled = featureFlagService.is(featureKeys.AUTHORING_BINDING_REAL_VALUE);

  const getAssets = async () => {
    const assetMap = AssetManager.getStored();
    return Object.entries(assetMap).filter(([, asset]) => !!asset);
  };

  const isV2AssetSchema = (schemas) => getSchemaVersion(schemas).major >= 2;

  const getExternalAssetFormatter = (schemas) =>
    isV2AssetSchema(schemas)
      ? async (urn) => {
        const asset = await AssetManager.resolveAsset(urn);

        if(!asset) {
          // eslint-disable-next-line no-console
          console.error(`No external asset returned for ${urn}`);
          return;
        }

        if (!asset.source?.original && asset.source?.url) {
          const url = asset.source.url;
          asset.source.original = { url };
          delete asset.source.url;
        }
        return [`"${urn}"`, asset];
      }
      : async (urn) => {
        const asset = await AssetManager.resolveAsset(urn);

        return [urn, asset.url || asset.original.url];
      };

  const getInternalAssetFormatter = (schemas) =>
    isV2AssetSchema(schemas)
      ? (urn, asset) => [`"${urn}"`, asset]
      : (urn, asset) => [urn, asset.url || asset.original.url];

  // @deprecated to be moved to configResolver.js
  const convert = async (value, target, internalAssetFormatter, externalAssetFormatter) => {
    const assets = await getAssets();

    const replacements = await Promise.all(assets.map(async ([urn, asset]) =>
      urn.startsWith(URN_EXTERNAL)
        ? externalAssetFormatter(urn)
        : internalAssetFormatter(urn, asset)
    ));

    const replaceItem = (doc, [original, replacement]) => {
      // TODO: fix in a pragmatic manner,
      // this is a temporary solution as fromJS changes the shape of asset
      // and config is getting corrupted
      const isReplacementString = typeof replacement === 'string';
      const stringifiedReplacement = isReplacementString ? replacement : JSON.stringify(fromJS(replacement));
      const from = target === ASSET ? original : stringifiedReplacement;
      const to = target === ASSET ? stringifiedReplacement : original;
      return doc.replace(from, to);
    };

    const asDoc = JSON.stringify(value);
    const replacedDoc = replacements.reduce(replaceItem, asDoc);
    return JSON.parse(replacedDoc);
  };

  const getInConfig = async (...args) => {
    const schemas = await service.getSchemasIn(args[0]);
    const originalConfig = await service.getInConfig.apply(null, args);
    if (!originalConfig) return originalConfig;
    const resolvedConfig = await ScopeBindingManager.resolveBoundReferences(args[0], args[1], RESOLVED, originalConfig);
    const resolveConfigWithAssets = await convert(resolvedConfig, ASSET, getInternalAssetFormatter(schemas), getExternalAssetFormatter(schemas));

    return resolveConfigWithAssets;
  };

  const saveInConfig = async (id, path, value) => {
    const schemas = await service.getSchemasIn(id);
    let converted = value;
    if (converted) {
      converted = await configResolver.resolve(id, path, converted, null, schemas, BOUND_REFERENCES);

      if (isBindingResolveToRealValueEnabled) {
        ScopeBindingManager.notify(id, converted);
      }
    }
    return service.saveInConfig.apply(null, [id, path, converted]);
  };

  const subscribeToConfig = (id, path, fn) => service.subscribeToConfig(
    id, path,
    async (originalConfig) => {
      const schemas = await service.getSchemasIn(id);

      const resolvedConfig = configResolver.resolve(id, path, undefined, originalConfig, schemas, RESOLVED);

      if (isBindingResolveToRealValueEnabled) {
        const targetIds = ScopeBindingManager.getTargetIdsFromConfig(originalConfig);

        for (const targetId of targetIds) {
          if(targetId) {
            const callback = async (data) => {
              const resolvedData = configResolver.resolve(id, path, undefined, originalConfig, schemas, RESOLVED);
              resolvedData.then((converted) => {
                fn(converted);
                // Every time we resolve a config, we should notify all listeners (if any).
                // This is required for chain binding
                // Eg if C1 is bound to C2 which is bound to C3.
                // Whenever C2 changes (due to changes to C3), we must notify C1 of this change in C2
                // We do this by calling notify with C2's id. This will trigger the C1 listener which is listening to C2's id
                !isEqual(data, converted) && ScopeBindingManager.notify(id, converted);
              });
            };
            ScopeBindingManager.register(targetId, callback, id);
          }
        }
      }
      const processedConfig = originalConfig ? resolvedConfig : Promise.resolve(originalConfig);

      processedConfig.then((converted) => {
        fn(converted);
      });

    }
  );

  const saveInScope = async (id, path, value) => {
    const schemas = await service.getSchemasIn(id);
    let converted = value;
    if (converted) {
      converted = await configResolver.resolve(id, path, value, null, schemas, null);
    }
    return service.saveInScope(id, path, converted);
  };

  const getCoursewareItem = (...args) => service.getCoursewareItem.apply(null, args);

  // TODO: need to resolve assets
  const getCoursewareState = () => configResolver.resolvePathwayItems();

  const getInCoursewareState = (path) => configResolver.resolve(undefined, path, undefined, undefined, undefined, RESOLVED);

  const getCoursewareRawState = async (coursewareId) => {
    const originalConfig = await this.service.getInConfig(coursewareId);
    return originalConfig;
  };

  const getInCoursewareRawState = async (path) => service.getInConfig(configData.id, path);

  const subscribeCoursewareProxy = (path, config) => configResolver.resolve(undefined, path, undefined, config, undefined, RESOLVED);

  return {
    getCoursewareItem,
    getInConfig,
    saveInConfig,
    saveInScope,
    subscribeToConfig,
    getCoursewareState,
    getInCoursewareState,
    getCoursewareRawState,
    getInCoursewareRawState,
    subscribeCoursewareProxy,
  };
};

export default (service, configData) => enhanceWithConfigResolver(service, configData);

